import React, { useEffect, useState, useCallback } from 'react';
import { compose } from 'ramda';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { showToast, BackTo, NoData } from '@deeptrue-frontend/ui';
import api from 'api/api';
import { Dashboard } from 'components/Layout';
import { useFormatMessage } from 'components/IntlProvider';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import ProjectMenu from 'components/Project/Common/ProjectMenu';
import { withProject, WithProjectProps } from 'components/Project/withProject';
import styled from 'styled-components/macro';

const { connect, selectors } = StoreProvider;

interface ProjectProps
  extends RouteComponentProps<{ id: string; codename: string }> {
  product: WithProjectProps['product'];
  project: WithProjectProps['project'];
  projectUUID: string;
  organization: OrganizationType;
  isProjectAccepted: WithProjectProps['isProjectAccepted'];
  isProjectArchived: WithProjectProps['isProjectArchived'];
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    border: '1px solid #CECECA',
    boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  file: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '95%',
  },
  aggregate: {
    margin: '0.2rem 0',
  },
  cancel: {
    backgroundColor: '#b00020',
    color: 'white',
    '&:hover': {
      backgroundColor: '#b00020b0',
    },
  },
  animContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '4rem',
    '& rect': {
      fill: 'transparent',
    },
  },
}));

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5rem;
`;

const ProjectMenuWrapper = styled.div`
  width: calc(100% - 2rem);
`;

const Files = ({
  project,
  projectUUID,
  organization,
  location,
  history,
  match: { params },
}: ProjectProps) => {
  const classes = useStyles();
  const t = useFormatMessage();
  const [files, setFiles] = useState<{ name: string }[]>([]);
  const [runId, setRunId] = useState<number | null>(null);

  const extractIdFromData = (data: any) =>
    data.results && data.results[0] ? data.results[0].id : null;

  const fetchData = useCallback(() => {
    const orgName = params.codename || organization.codename;

    projectUUID &&
      api.resources
        .getProjectTableResults(orgName, projectUUID)
        .then(({ data }) => {
          const tableId = extractIdFromData(data);

          tableId &&
            api.resources
              .getProjectTableRuns(orgName, tableId)
              .then(({ data }) => {
                const runId = extractIdFromData(data);

                runId &&
                  api.resources
                    .getProjectRunFiles(orgName, runId)
                    .then(({ data }) => {
                      if (data.files) {
                        setFiles(data.files);
                        setRunId(runId);
                      }
                    });
              });
        });
  }, [organization.codename, projectUUID, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onDownloadFileCsv = (name: string) => {
    api.resources
      .getFileCsv(
        params.codename || organization.codename,
        runId as number,
        name
      )
      .then(({ data }) => {
        const url = window.URL.createObjectURL(new Blob([data]));

        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  const overviewUrl = params.codename
    ? `/admin/organizations/${params.codename}/projects/`
    : `/`;

  return (
    <Dashboard
      title={project.name || t('page.project', 'Project details')}
      location={location}
      combinedTitle={project.combinedTitle}
    >
      <BackTo onClick={() => history.push(overviewUrl)}>
        {t('project.backTo.overview', 'Back to overview')}
      </BackTo>
      <ProjectMenuWrapper>
        <ProjectMenu
          id={project.id}
          projectStatus={project.status}
          isProjectLoaded={project.id !== -1}
        />
      </ProjectMenuWrapper>
      <div style={{ marginTop: '2rem', width: '100%' }}>
        {files.length ? (
          files.map((file: { name: string }) => (
            <Grid
              item
              xs={12}
              md={4}
              key={file.name}
              onClick={() => onDownloadFileCsv(file.name)}
            >
              <Paper className={classes.paper}>
                <span className={classes.file}>{file.name}</span>
              </Paper>
            </Grid>
          ))
        ) : (
          <Wrapper>
            <NoData
              title={t(
                'project.statistic.bexio.nodata.report.title',
                'Waiting for survey data'
              )}
              description={t(
                'project.statistic.nodata.files.description',
                'We will now automatically send the survey to your audience. As soon as the first responses are available, the files will be generated.'
              )}
            />
          </Wrapper>
        )}
      </div>
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    projectUUID: selectors.projects.selected(state).uuid,
  })),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: ProjectProps) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(Files);
